// Shared "chrome" (standardized header + page-number footer) for pdf-lib reports.
// Mirrors src/utils/reports/pdfChrome.js (the jsPDF version) so both libraries
// produce the same standardized look.
//
// pdf-lib uses a bottom-left origin (y increases upward).
import { rgb } from "pdf-lib";
import { format } from "date-fns";

export const formatGeneratedAt = (d = new Date()) => format(d, "MMM d, yyyy h:mm a");

export const dateRangeText = (dateRange) => {
  if (!dateRange) return "";
  if (typeof dateRange === "string") return dateRange;
  const { start, end } = dateRange;
  if (!start || !end) return "";
  return `${format(start, "MMM d, yyyy")} – ${format(end, "MMM d, yyyy")}`;
};

/**
 * Draw the standardized header on a pdf-lib page.
 * @param {PDFPage} page
 * @param {object} opts
 * @param {string} opts.title
 * @param {{start:Date,end:Date}|string} [opts.dateRange]
 * @param {string} [opts.generatedBy]
 * @param {Date}   [opts.generatedAt]
 * @param {PDFFont} opts.font       regular font
 * @param {PDFFont} opts.boldFont   bold font
 * @returns {number} Y coordinate where body content can begin (going downward).
 */
export const drawReportHeaderLib = (
  page,
  {
    title = "Report",
    dateRange,
    generatedBy = "Unknown",
    generatedAt = new Date(),
    font,
    boldFont,
  } = {}
) => {
  const { width, height } = page.getSize();
  const marginX = 50;
  const black = rgb(0, 0, 0);

  // Top-right: generation date + user
  const g1 = `Generated: ${formatGeneratedAt(generatedAt)}`;
  const g2 = `By: ${generatedBy}`;
  const gs = 9;
  page.drawText(g1, {
    x: width - marginX - font.widthOfTextAtSize(g1, gs),
    y: height - 32,
    size: gs,
    font,
    color: black,
  });
  page.drawText(g2, {
    x: width - marginX - font.widthOfTextAtSize(g2, gs),
    y: height - 44,
    size: gs,
    font,
    color: black,
  });

  // Centered title
  const ts = 18;
  page.drawText(title, {
    x: (width - boldFont.widthOfTextAtSize(title, ts)) / 2,
    y: height - 76,
    size: ts,
    font: boldFont,
    color: black,
  });

  // Centered date range (optional)
  let dividerY = height - 94;
  const rangeStr = dateRangeText(dateRange);
  if (rangeStr) {
    const rs = 11;
    page.drawText(rangeStr, {
      x: (width - font.widthOfTextAtSize(rangeStr, rs)) / 2,
      y: height - 94,
      size: rs,
      font,
      color: black,
    });
    dividerY = height - 110;
  }

  page.drawLine({
    start: { x: marginX, y: dividerY },
    end: { x: width - marginX, y: dividerY },
    thickness: 0.75,
    color: rgb(0.78, 0.78, 0.78),
  });

  return dividerY - 18; // content top (going downward)
};

/**
 * Stamp "Page X of X" centered at the bottom of every page.
 * @param {PDFDocument} pdfDoc
 * @param {PDFFont} font
 */
export const stampPageNumbersLib = (pdfDoc, font) => {
  const pages = pdfDoc.getPages();
  const total = pages.length;
  pages.forEach((page, idx) => {
    const { width } = page.getSize();
    const label = `Page ${idx + 1} of ${total}`;
    const s = 9;
    page.drawText(label, {
      x: (width - font.widthOfTextAtSize(label, s)) / 2,
      y: 24,
      size: s,
      font,
      color: rgb(0.47, 0.47, 0.47),
    });
  });
};
